import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/store";

import SidepeekDetailsAccordion from "../../../components/ui/accordion/v2/SidepeekDetailsAccordion";
import EditableField from "../../../components/ui/formelement/v2/EditableField";

import {
  getStakeholderById,
  getEnrichedStakeholderById,
} from "../logic/stakeholderSlice";

import {
  updateStakeholder,
  updateBusinessKeyPersonnelDetails,
} from "../logic/stakeholderThunks";

import { showErrorToast } from "../../../lib/toast";

interface KeyPersonnelDetailsProps {
  stakeholderId: string;
}

const KeyPersonnelDetails: React.FC<KeyPersonnelDetailsProps> = ({
  stakeholderId,
}) => {
  const { id: caseId } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();

  const [editingField, setEditingField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const stakeholder = useAppSelector((state) =>
    getStakeholderById(state, stakeholderId),
  );

  const enrichedStakeholder = useAppSelector((state) =>
    getEnrichedStakeholderById(state, stakeholderId),
  );

  if (!stakeholder) {
    return null;
  }

  const businessKeyPersonnelDetails =
    enrichedStakeholder?.business_key_personnel_details;

  const handleSave = async (
    field: string,
    value: string | null,
  ): Promise<boolean> => {
    if (!caseId) {
      return false;
    }

    setIsSaving(true);
    const trimmedValue = value?.trim() || undefined;

    try {
      if (field === "name") {
        await dispatch(
          updateStakeholder({
            caseId,
            stakeholderId,
            request: {
              name: trimmedValue || "",
            },
          }),
        ).unwrap();
      } else if (field === "din") {
        await dispatch(
          updateBusinessKeyPersonnelDetails({
            caseId,
            stakeholderId,
            request: {
              din: trimmedValue,
            },
          }),
        ).unwrap();
      }

      setEditingField(null);
      return true;
    } catch {
      showErrorToast(
        "Failed to update key personnel details. Please try again.",
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SidepeekDetailsAccordion title="Details">
      <div className="flex w-full flex-col gap-2">
        <EditableField
          label="Name"
          value={stakeholder.name}
          type="text"
          isEditable={true}
          isEditing={editingField === "name"}
          onEditStart={() => setEditingField("name")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("name", value)}
          disabled={isSaving}
        />

        <EditableField
          label="DIN"
          value={businessKeyPersonnelDetails?.din ?? null}
          type="din"
          isEditable={true}
          isEditing={editingField === "din"}
          onEditStart={() => setEditingField("din")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("din", value)}
          disabled={isSaving}
        />
      </div>
    </SidepeekDetailsAccordion>
  );
};

export default KeyPersonnelDetails;